"use client";

import {
  type MouseEvent,
  type ReactNode,
  type ReactElement,
  useState,
  useEffect,
  type FC,
  type Ref,
} from "react";
import { motion } from "motion/react";
import { Card } from "../Card";
import { Flex } from "../Flex";
import { Icon, type IconName, type IconProps } from "../Icon";
import { IconButton } from "../IconButton";
import { Indicator } from "../Indicator";
import { Text } from "../Text";
import { Tooltip } from "../Tooltip";
import { isString, mergeClasses } from "@huub-web/utils";

import type { ClassNameProp } from "../../props";

import {
  poiCardStyle,
  poiCloseStyle,
  poiContainerStyle,
  poiContentStyle,
  poiStyle,
  poiTitleStyle,
} from "./POI.css";
import { elevations } from "../../theme/css/elevations.css";

export type POIProps = ClassNameProp & {
  delay?: number;
  tooltipHideDelay?: number;
  animate?: boolean;
  left?: number;
  top?: number;
  tooltip?: ReactNode;
  onClick?: (e: MouseEvent<HTMLDivElement>) => void;
  icon?: IconName | ReactElement<any>;
  iconColor?: IconProps["color"];
  /**
   * Whether the POI is in active state (e.g. route is displayed)
   */
  active?: boolean;
  /**
   * Title for card-style tooltip (shown when active)
   */
  title?: string;
  /**
   * Subtitle for card-style tooltip (shown when active)
   */
  subtitle?: ReactNode;
  /**
   * If true, shows a close button on the card tooltip
   */
  closable?: boolean;
  /**
   * Callback when close button is clicked
   */
  onClose?: () => void;
  /**
   * Ref forwarded to the root element
   */
  ref?: Ref<HTMLDivElement>;
};

export const POI: FC<POIProps> = ({
  left,
  top,
  animate = true,
  delay,
  tooltipHideDelay,
  iconColor,
  tooltip,
  icon,
  onClick,
  active,
  title,
  subtitle,
  closable,
  onClose,
  className,
  ref,
}) => {
  const [showTooltip, setShowTooltip] = useState(true);

  /**
   * Reset tooltip visibility when active becomes true with a title (card popover)
   * This ensures the card shows again after being closed and re-selected
   */
  useEffect(() => {
    if (active && title) {
      setShowTooltip(true);
    }
  }, [active, title]);

  useEffect(() => {
    if (!tooltipHideDelay) {
      return;
    }
    setTimeout(() => {
      setShowTooltip(false);
    }, tooltipHideDelay * 1000);
  }, [tooltipHideDelay]);

  /**
   * Handle close button click
   */
  const handleClose = () => {
    setShowTooltip(false);
    onClose?.();
  };

  // Use card-style tooltip when title is provided
  const hasCardTooltip = !!title;

  return (
    <div
      className={poiContainerStyle}
      style={{
        left: left ? `${left}%` : undefined,
        top: top ? `${top}%` : undefined,
      }}
    >
      {hasCardTooltip ? (
        <>
          {showTooltip && (
            <Card
              className={poiCardStyle({ closable })}
              edgeDirection="horizontal"
              contentClassName={poiContentStyle}
              elevated
              padding="s"
            >
              <Indicator
                icon={icon || "pin"}
                type="square"
                variant="tertiarySurface"
              />
              <Flex direction="column" gap="xxs" grow>
                <Text variant="bodyM" bold clamp={2} className={poiTitleStyle}>
                  {title}
                </Text>
                {subtitle &&
                  (isString(subtitle) ? (
                    <Text variant="bodyS" color="currentColor50">
                      {subtitle}
                    </Text>
                  ) : (
                    subtitle
                  ))}
              </Flex>
              {closable && (
                <IconButton
                  variant="opaque"
                  icon="close"
                  size="xxs"
                  onClick={handleClose}
                  className={poiCloseStyle}
                  border="default"
                  rounded
                />
              )}
            </Card>
          )}
          <motion.div
            ref={ref}
            aria-hidden="true"
            className={mergeClasses(
              poiStyle({ hoverable: !!onClick, active }),
              elevations.m,
              className,
            )}
            initial={
              animate
                ? {
                    opacity: 0,
                    scale: 0.4,
                    translate: "translate(-50%, 50%)",
                  }
                : { opacity: 1, scale: 1, translate: "translate(-50%, 50%)" }
            }
            animate={
              animate
                ? { opacity: 1, scale: 1, translate: "translate(-50%, 50%)" }
                : {
                    opacity: 0,
                    scale: 0.4,
                    translate: "translate(-50%, 50%)",
                  }
            }
            transition={{
              duration: 0.2,
              ease: [0.25, 0.1, 0.25, 1],
              delay,
            }}
            onClick={onClick}
          >
            <Icon icon={icon} color={active ? "onPrimary" : iconColor} />
          </motion.div>
        </>
      ) : (
        <Tooltip value={showTooltip ? tooltip : undefined}>
          <motion.div
            ref={ref}
            aria-hidden="true"
            className={mergeClasses(
              poiStyle({ hoverable: !!onClick, active }),
              elevations.m,
              className,
            )}
            initial={
              animate
                ? {
                    opacity: 0,
                    scale: 0.4,
                    translate: "translate(-50%, 50%)",
                  }
                : { opacity: 1, scale: 1, translate: "translate(-50%, 50%)" }
            }
            animate={
              animate
                ? { opacity: 1, scale: 1, translate: "translate(-50%, 50%)" }
                : {
                    opacity: 0,
                    scale: 0.4,
                    translate: "translate(-50%, 50%)",
                  }
            }
            transition={{
              duration: 0.2,
              ease: [0.25, 0.1, 0.25, 1],
              delay,
            }}
            onClick={onClick}
          >
            <Icon icon={icon} color={active ? "onPrimary" : iconColor} />
          </motion.div>
        </Tooltip>
      )}
    </div>
  );
};
